import { Refresh } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
// @mui material components
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import { Box } from '@mui/system';
import CustomSnackbar from 'components/CustomSnackbar';
import CsvDownloadWizard from 'components/CsvDownloadWizard';
import SensorHealth from 'components/DeviceHealth/SensorHealth.js';
import DialogSelect from 'components/Dialog/DialogSelect.js';
import GenericModal from 'components/GenericModal';
import Loading from 'components/Loading/Loading.js';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { getDelayUntilNextFiveMinute59 } from 'helpers/helpers.js';
import useAuth from 'hooks/useAuth';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  clearSensorData,
  fetchAlerts,
  fetchCurrentData,
  fetchRangeData,
  setChartPopUp,
  setSensorTimeRangeValue,
} from 'stores/sensor/sensorStore';
import { brandColors } from 'types/colors';
import { Measurement } from 'types/measurement';
import { TimeInMS } from 'types/time';
import { AlertSummary } from '../../components/Alerts/AlertSummary';
import Charts, { getSensorChartDataset } from './Charts.js';
import FormattedChart from './components/FormattedChart';
import GraphsFilterBar from './components/GraphsFilterBar';
import { CurrentData } from './CurrentData';
import ErrorIcon from '@mui/icons-material/Error';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { getRangeData } from 'services/dataService';

const chartFields = [Measurement.CAL, Measurement.Co2Ratio, Measurement.Co2];

// These are all sensor CSV columns that may be available for some sensor models.
const csvColumns = [
  Measurement.Co2Ratio,
  Measurement.CAL,
  Measurement.COMFORT_LEVEL,
  Measurement.Co2,
  Measurement.RelativeHumidity,
  Measurement.Temperature,
  Measurement.PM2,
  Measurement.PM10,
  Measurement.TVOC,
  Measurement.occupancy,
  'nox',
  'motion_counter',
  Measurement.DpMean,
  Measurement.thermal_justified,
  Measurement.thermal_saveable,
  Measurement.thermal_requested,
  Measurement.air_justified,
  Measurement.air_saveable,
  Measurement.air_requested,
];

// These labels are shown to the user instead of raw database column names.
const csvColumnLabels = {
  [Measurement.Co2Ratio]: 'Ventilation',
  [Measurement.CAL]: 'Clean Air Level',
  [Measurement.COMFORT_LEVEL]: 'Comfort Level',
  [Measurement.Co2]: 'CO2',
  [Measurement.RelativeHumidity]: 'Relative Humidity',
  [Measurement.Temperature]: 'Temperature',
  [Measurement.PM2]: 'PM 2.5',
  [Measurement.PM10]: 'PM 10',
  [Measurement.TVOC]: 'TVOC',
  [Measurement.occupancy]: 'Occupancy',
  nox: 'NOx',
  motion_counter: 'Motion',
  [Measurement.DpMean]: 'Differential Pressure',
  [Measurement.thermal_justified]: 'Thermal justified',
  [Measurement.thermal_saveable]: 'Thermal saveable',
  [Measurement.thermal_requested]: 'Thermal requested',
  [Measurement.air_justified]: 'Air justified',
  [Measurement.air_saveable]: 'Air saveable',
  [Measurement.air_requested]: 'Air requested',
};

const CurrentDataMeasurments = [
  Measurement.CAL,
  Measurement.Co2,
  Measurement.Co2Ratio,
  Measurement.RelativeHumidity,
  Measurement.Temperature,
  Measurement.PM2,
  Measurement.PM10,
  Measurement.COMFORT_LEVEL,
  Measurement.TVOC,
  Measurement.occupancy,
  Measurement.thermal_justified,
  Measurement.thermal_saveable,
  Measurement.thermal_requested,
  Measurement.air_justified,
  Measurement.air_saveable,
  Measurement.air_requested,
  'nox',
];

/**
 * The refresh rate that symbolize that auto refresh is off
 */
const REFRESH_RATE_OFF = 0;

// Values in MS
const refreshRateOptions = [
  { label: 'Off', value: REFRESH_RATE_OFF },
  { label: '1 Minute', value: TimeInMS.Minute },
  { label: '5 Minutes', value: TimeInMS.Minute * 5 },
  { label: '10 Minutes', value: TimeInMS.Minute * 10 },
];

// last 12 months
const TimeRangeOptions = [
  // { label: "1 Minute", value: TimeInMS.Minute },
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: '1 Month', value: TimeInMS.Month },
  { label: 'date to date', value: 'date to date' },
];
//#endregion

const INACTIVE_BACKGROUND_COLOR = '#5e5e5e';
const INACTIVE_THRESHOLD_SECONDS = 900;

const topBarButtonSx = {
  width: '4rem',
  minWidth: '4rem',
  height: '2rem',
  p: 0,
  // border: '2px solid #fff',
  borderRadius: '7px',
  boxShadow: 'none',
  color: '#fff',
  transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'scale(1)',
  '&:hover': {
    // color: brandColors.BLUE,
    boxShadow: 'none',
    // backgroundColor: '#fff',
    // scale: '1.1',
    transform: 'scale(1.2)',
  },
};

const topBarIconSx = {
  settings: { scale: '1.8' },
  queryStats: { scale: '2.2' },
  download: { scale: '1.5', ml: 0.5 },
  refresh: { fontSize: '1.5rem' },
};

// The navigation bar uses a high stacking order, so render these portaled
// tooltips above it rather than applying z-index to the Tooltip wrapper.
const sensorTooltipProps = {
  slotProps: {
    popper: {
      sx: { zIndex: 50020 },
    },
  },
};

// TODO: add choosing bucket ?

function SensorPage(props) {
  // const { auth } = useAuth();
  // let refreshRate = refreshRateOptions[0];
  const dispatch = useDispatch();
  const sensorId = useParams().id;
  const selectedGraphs = useSelector(state => state.sensors.selectedGraphs);
  const currentUser = useSelector(state => state.sensors.currentUser);
  const data = useSelector(state => state.sensors.data);
  // console.log('data:', data);
  const lastMeasurements = useSelector(state => state.sensors.data.lastMeasurements);
  const lastUpdate = useSelector(state => state.sensors.data.lastUpdate);
  const sensorName = useSelector(state => state.sensors.data.sensorName);
  const building = useSelector(state => state.sensors.data.building);
  const devices = useSelector(state => state.sensors.devices);
  const chartPopUp = useSelector(state => state.sensors.chartPopUp);
  const rangeObj = useSelector(state => state.sensors.timeRange);
  const [loading, setLoading] = useState(false);
  const [hasFetchedRangeData, setHasFetchedRangeData] = useState(null);
  const [triggerDownloadAlertsCsv, setTriggerDownloadAlertsCsv] = useState(0);
  const refreshCurrDataRef = useRef(null);
  const timeoutRef = useRef(null);
  const skipNextRangeRefreshRef = useRef(false);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isFirstUpdateOflastSensorUpdate = useRef(true);
  const navigate = useNavigate();
  const [hasNoxData, setHasNoxData] = useState(false);
  const [hasRadarData, setHasRadarData] = useState(false);
  const [hasDpMeanData, setHasDpMeanData] = useState(false);
  const [deviceType, setDeviceType] = useState('Sensor-SEN54');
  const [mobileChartsOpen, setMobileChartsOpen] = useState(false);
  const [mobileChartsView, setMobileChartsView] = useState('filters');
  const [csvWizardOpen, setCsvWizardOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const matchedDevice = Array.isArray(devices) ? devices.find(d => String(d?.id) === String(sensorId)) : null;
  const buildingId = data.building_id || matchedDevice?.building_id;
  // Only show NOx and Motion when this sensor actually has those measurements.
  const availableCsvColumns = csvColumns.filter(column => {
    if (column === 'nox') return hasNoxData;
    if (column === 'motion_counter') return hasRadarData;
    if (column === Measurement.DpMean) return hasDpMeanData;
    return true;
  });
  const isInactive =
    ((lastUpdate !== undefined && lastUpdate > 0) || lastUpdate === 0 || lastUpdate === null) &&
    Math.floor(Date.now() / 1000) - Number(lastUpdate) > INACTIVE_THRESHOLD_SECONDS;
  const topBarBackgroundColor = isInactive ? INACTIVE_BACKGROUND_COLOR : brandColors.BLUE;
  const topBarColorSx = {
    backgroundColor: topBarBackgroundColor,
    '&:hover': {
      backgroundColor: topBarBackgroundColor,
    },
  };
  const sensorStatusButtonSx = [topBarButtonSx, topBarColorSx];

  const handleBuildingClick = () => {
    if (!buildingId) {
      return;
    }
    navigate(`/manage-buildings/${buildingId}`);
  };

  const renderSensorDisplayName = () => (
    <MDTypography variant="h4" color="light">
      {building ? (
        <>
          <Box
            component="span"
            onClick={handleBuildingClick}
            sx={{
              cursor: buildingId ? 'pointer' : 'inherit',
              textDecoration: 'none',
              textUnderlineOffset: '3px',
            }}>
            {building}
          </Box>
          {' / '}
        </>
      ) : null}
      {data.sensorName}
    </MDTypography>
  );

  useEffect(() => {
    if (rangeObj.label === 'date to date') {
      let nowObj = new Date();
      const thisMonthStr = (nowObj.getMonth() + 1).toString().padStart(2, '0');
      const thisMonthNum = parseInt(thisMonthStr, 10);
      const selectedTimeFrameMonthNum = parseInt(rangeObj.value.slice(5, 7), 10);
    }
    if (skipNextRangeRefreshRef.current) {
      skipNextRangeRefreshRef.current = false;
      return;
    }
    refreshContent();
  }, [rangeObj, selectedGraphs]);

  const refreshContent = () => {
    // setRefreshGlow(false);
    updateCurrentData();
    updateRangeData();
    updateAlerts();
    getUpdateLastUpdateTime();
  };

  const refreshCurrData = () => {
    updateCurrentData();
    getUpdateLastUpdateTime();
  };

  const changeTimeRange = range => {
    // console.log("changeTimeRange triggered");
    dispatch(setSensorTimeRangeValue(range));
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getChartQuickRange = rangeKey => {
    if (rangeKey === 'yesterday') {
      const yesterday = DateTime.now().minus({ days: 1 });
      const yesterdayValue = yesterday.toFormat('yyyy-MM-dd');

      return {
        label: 'Yesterday',
        value: `${yesterdayValue}_${yesterdayValue}`,
      };
    }

    if (rangeKey === 'week') {
      return { label: '1 Week', value: TimeInMS.Week };
    }

    if (rangeKey === 'last3Hours') {
      return { label: 'Last 3 Hours', value: TimeInMS.Hour * 3 };
    }

    if (rangeKey === 'last6Hours') {
      return { label: 'Last 6 Hours', value: TimeInMS.Hour * 6 };
    }

    if (rangeKey === 'last12Hours') {
      return { label: 'Last 12 Hours', value: TimeInMS.Hour * 12 };
    }

    if (rangeKey === 'last2Days') {
      return { label: 'Last 2 Days', value: TimeInMS.Day * 2 };
    }

    return { label: 'Today', value: TimeInMS.Day };
  };

  const chartRangeButtons = [
    { label: 'Last 3 Hours', rangeKey: 'last3Hours', isActive: rangeObj.label === 'Last 3 Hours' },
    { label: 'Last 6 Hours', rangeKey: 'last6Hours', isActive: rangeObj.label === 'Last 6 Hours' },
    { label: 'Last 12 Hours', rangeKey: 'last12Hours', isActive: rangeObj.label === 'Last 12 Hours' },
    { label: 'Today', rangeKey: 'today', isActive: rangeObj.label === 'Today' },
    { label: 'Yesterday', rangeKey: 'yesterday', isActive: rangeObj.label === 'Yesterday' },
    { label: 'Last 2 Days', rangeKey: 'last2Days', isActive: rangeObj.label === 'Last 2 Days' },
    { label: '1 Week', rangeKey: 'week', isActive: rangeObj.label === '1 Week' },
  ];

  const handleChartRangeSelect = button => {
    const range = getChartQuickRange(button.rangeKey);

    skipNextRangeRefreshRef.current = true;
    dispatch(setSensorTimeRangeValue(range));
    updateRangeData(range.value);
    updateAlerts(range.value);
  };

  const updateRangeData = async (timeRangeValue = rangeObj.value) => {
    setLoading(true);
    setHasFetchedRangeData(null);

    try {
      const res = await dispatch(
        fetchRangeData({
          measurements: selectedGraphs,
          sensorId: sensorId,
          timeRange: timeRangeValue,
          navigate,
          userTimeZone: userTimeZone,
        }),
      ).unwrap();

      setHasFetchedRangeData(
        Object.values(res.data.measurements ?? {}).some(measurements => Array.isArray(measurements) && measurements.length > 0),
      );
      setDeviceType(res.data.deviceType);
      if (res.data.hasNox) {
        setHasNoxData(true);
      }
      if (res.data.hasRadar) {
        setHasRadarData(true);
      }
      setHasDpMeanData(Boolean(res.data.hasDpMean));
    } catch (e) {
      if (e?.statusCode === 403) {
        navigate('/unauthorized', { replace: true });
        return;
      }
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const updateAlerts = (timeRangeValue = rangeObj.value) => {
    dispatch(
      fetchAlerts({
        // auth: auth,
        sensorId: sensorId,
        timeRange: timeRangeValue,
        userTimeZone: userTimeZone,
        forPage: 'sensor',
      }),
    );
  };

  const updateCurrentData = () => {
    dispatch(
      fetchCurrentData({
        // auth: auth,
        measurements: CurrentDataMeasurments,
        sensorId: sensorId,
      }),
    );
  };

  useEffect(() => {
    const delay = getDelayUntilNextFiveMinute59();
    timeoutRef.current = setTimeout(() => {
      refreshCurrData();
      refreshCurrDataRef.current = setInterval(refreshCurrData, 720000);
    }, delay);

    // const updateRangeDataId = setInterval(updateRangeData, 3600000);

    return () => {
      dispatch(clearSensorData());
      // Clear any old refresh rate
      clearTimeout(timeoutRef);
      clearInterval(refreshCurrDataRef);
      // clearInterval(updateRangeDataId);
    };
  }, []);

  const getLastValue = measurement => {
    // this function was previously called: getCurrentValue
    const nowSeconds = Math.floor(Date.now() / 1000);

    try {
      if (lastMeasurements !== undefined && Object.keys(lastMeasurements).length !== 0 && lastUpdate !== 0) {
        // here we are checking if the last sensor record in the DB is in the last 10 min.
        if (lastUpdate < nowSeconds - 900) {
          return 'N/A';
        } else {
          return lastMeasurements[measurement];
        }
      }
      return 'N/A';
    } catch (err) {
      console.error(err);
    }
  };

  const getUpdateLastUpdateTime = () => {
    if (lastUpdate > 0 || lastUpdate === 0) {
      const topBar = document.getElementById('sensorPageTopBar');
      if (topBar) {
        const date = new Date(lastUpdate * 1000);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = String(date.getFullYear()).slice(-2);

        return `${hours}:${minutes} - ${day}.${month}.${year}`;
      }
    }

    // return new Date().toLocaleString("en-GB");
    return 'N/A';
  };

  const handleDownloadCSV = () => setCsvWizardOpen(true);

  const loadCsvMeasurements = (columns, timeframe, options) =>
    getRangeData(columns, sensorId, timeframe, userTimeZone, options?.includeRawRecords);

  // console.log(chartPopUp);
  return (
    <DashboardLayout>
      <DashboardNavbar objectName={data.sensorName} />
      {loading && <Loading />}
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={5000}
        vertical="top"
        horizontal="center"
      />
      <GenericModal
        open={csvWizardOpen}
        onClose={() => setCsvWizardOpen(false)}
        title="Download charts data"
        Component={CsvDownloadWizard}
        componentProps={{
          deviceId: sensorId,
          deviceName: sensorName,
          // Pass filtered columns so unsupported sensor data cannot be selected.
          availableColumns: availableCsvColumns,
          selectedColumns: selectedGraphs,
          columnLabels: csvColumnLabels,
          currentTimeRange: rangeObj.value,
          firstRecordTime: data.firstUpdate,
          fetchMeasurements: loadCsvMeasurements,
          onDownloadSuccess: () => setSnack({ open: true, message: 'CSV file downloaded successfully.', severity: 'success' }),
          onComplete: () => setCsvWizardOpen(false),
        }}
      />
      <Dialog open={chartPopUp !== undefined} fullWidth maxWidth="xl" sx={{ zIndex: 100000 }}>
        <DialogTitle sx={{ padding: '0' }}>
          <IconButton
            aria-label="close"
            // onClick={() => setChartPopUp(undefined)}
            onClick={() => dispatch(setChartPopUp(undefined))}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '50px' }}>
          {chartPopUp ? (
            <FormattedChart
              title={chartPopUp?.title}
              datasets={Object.keys(minMaxRangeMeasurements).map(name =>
                getSensorChartDataset(minMaxRangeMeasurements[name], name),
              )}
              isRange={chartPopUp?.isRange}
              setChartPopUp={setChartPopUp}
              isPopUp={true}
              onChartRangeSelect={handleChartRangeSelect}
              chartRangeButtons={chartRangeButtons}
            />
          ) : undefined}
        </DialogContent>
      </Dialog>
      <Dialog
        className="charts-select-dialog"
        open={mobileChartsOpen}
        onClose={() => setMobileChartsOpen(false)}
        fullScreen={isSmallScreen}
        sx={{
          zIndex: 100000,
          '& .MuiDialog-paper': {
            width: { xs: '100vw', sm: mobileChartsView === 'filters' ? 'fit-content' : 'min(90vw, 1000px)' },
            height: { xs: '100vh', sm: mobileChartsView === 'filters' ? 'fit-content' : 'min(85vh, 720px)' },
            maxWidth: { xs: '100vw', sm: mobileChartsView === 'filters' ? 'calc(100vw - 32px)' : '90vw' },
            maxHeight: { xs: '100vh', sm: mobileChartsView === 'filters' ? 'calc(100dvh - 32px)' : '85vh' },
            m: { xs: 0, sm: 2 },
            overflow: { xs: 'hidden', sm: mobileChartsView === 'filters' ? 'auto' : 'hidden' },
            backgroundColor: '#fff',
          },
        }}>
        <DialogTitle sx={{ p: 0, position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
          <IconButton aria-label="close charts" onClick={() => setMobileChartsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            ...(mobileChartsView === 'charts'
              ? {
                  position: { xs: 'absolute', sm: 'relative' },
                  top: { xs: 0, sm: 'auto' },
                  left: { xs: 0, sm: 'auto' },
                  flex: 'none',
                  '@media (max-width: 599.95px) and (orientation: portrait)': {
                    width: '100dvh',
                    height: '100dvw',
                    transform: 'rotate(90deg) translateY(-100%)',
                    transformOrigin: 'top left',
                  },
                  '@media (max-width: 599.95px) and (orientation: landscape)': {
                    width: '100dvw',
                    height: '100dvh',
                  },
                  width: { xs: '100dvw', sm: '100%' },
                  height: { xs: '100dvh', sm: '100%' },
                }
              : {
                  width: { xs: '100dvw', sm: 'fit-content' },
                  height: { xs: '100dvh', sm: 'fit-content' },
                }),
          }}>
          <Box
            sx={{
              width: mobileChartsView === 'filters' ? { xs: '100%', sm: 'fit-content' } : '100%',
              height: mobileChartsView === 'filters' ? { xs: '100%', sm: 'fit-content' } : '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              overflow: { xs: 'hidden', sm: mobileChartsView === 'filters' ? 'visible' : 'hidden' },
              p: mobileChartsView === 'charts' ? 0.5 : 1,
              pt: mobileChartsView === 'charts' ? 0.5 : 4,
              '& > .MuiFormGroup-root': {
                flex: '1 1 auto',
                minHeight: 0,
                overflow: 'hidden',
              },
              '& .MuiFormControlLabel-root': {
                m: 0,
                mr: mobileChartsView === 'filters' ? 0 : 1,
              },
              '& .MuiFormControlLabel-label': {
                fontSize: '1.1rem',
              },
              '& .chart-container': {
                flex: '1 1 auto',
                minHeight: 0,
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                p: 0,
                m: 0,
              },
              '& .MuiCard-root': {
                borderRadius: mobileChartsView === 'charts' ? 0 : undefined,
                boxShadow: mobileChartsView === 'charts' ? 'none' : undefined,
              },
              '& .solin-line-chart': {
                overflow: 'hidden',
              },
            }}>
            {mobileChartsView === 'filters' ? (
              <GraphsFilterBar
                isMobileMode={isSmallScreen}
                isChartSelectionDialog
                showRefresh={false}
                hasNox={hasNoxData}
                hasRadar={hasRadarData}
                hasDpMean={hasDpMeanData}
                hideTvoc={deviceType === 'Sensor-SPS30'}
                onConfirm={() => {
                  if (isSmallScreen) {
                    setMobileChartsView('charts');
                  } else {
                    setMobileChartsOpen(false);
                  }
                }}
              />
            ) : (
              <Charts compact onChartRangeSelect={handleChartRangeSelect} chartRangeButtons={chartRangeButtons} />
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <MDBox pt={1} pb={1}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MDBox
              mb={1}
              px={2}
              variant="gradient"
              borderRadius="lg"
              coloredShadow="light"
              id="sensorPageTopBar"
              style={{
                background: topBarBackgroundColor,
                marginTop: isSmallScreen ? '3.5rem' : 0,
              }}>
              {isSmallScreen ? (
                <Stack direction="column" alignItems="stretch" justifyContent="space-between" spacing={1} pt={1}>
                  <Stack
                    direction="column"
                    marginLeft={0}
                    spacing={2}
                    alignItems="flex-start"
                    className="top-bar"
                    sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex' }}>
                      {renderSensorDisplayName()}
                      <Icon fontSize="large" style={{ color: 'white' }}>
                        {isInactive ? 'sensors_off' : 'sensors'}
                      </Icon>
                    </Box>
                    {lastUpdate !== 0 && (
                      <MDTypography variant="subtitle" color="white">
                        Last Update: {getUpdateLastUpdateTime()}
                      </MDTypography>
                    )}
                    {loading && <CircularProgress style={{ color: 'white' }} />}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0}
                    className="top-bar-actions"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, max-content)',
                      gridAutoRows: 'max-content',
                      columnGap: 1,
                      rowGap: 1,
                      alignItems: 'center',
                      width: '100%',
                      justifyContent: 'center',
                      justifyItems: 'center',
                      overflowX: 'hidden',
                      whiteSpace: 'nowrap',
                      pb: 0.5,
                      '& > :not(style) + :not(style)': {
                        marginLeft: 0,
                      },
                    }}>
                    <Tooltip {...sensorTooltipProps} title="Edit sensor info" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={() => {
                          deviceType.startsWith('Controller')
                            ? navigate(`/manage-device/controller/${sensorId}`)
                            : navigate(`/manage-device/sensor/${sensorId}`);
                        }}>
                        <SettingsIcon sx={topBarIconSx.settings} />
                      </Button>
                    </Tooltip>
                    <Tooltip {...sensorTooltipProps} title="See charts" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={() => {
                          setMobileChartsView('filters');
                          setMobileChartsOpen(true);
                        }}>
                        <TimelineIcon sx={{ scale: '2.4' }} />
                        <OpenInNewIcon sx={{ scale: '1.4', ml: 0.2, mt: 1.5 }} />
                      </Button>
                    </Tooltip>
                    <Tooltip {...sensorTooltipProps} title="Download alerts to csv file" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={() => setTriggerDownloadAlertsCsv(prev => prev + 1)}>
                        <ErrorIcon sx={{ scale: '1.6' }}></ErrorIcon>
                        <DownloadIcon sx={topBarIconSx.download}></DownloadIcon>
                      </Button>
                    </Tooltip>
                    <Tooltip {...sensorTooltipProps} title="Download charts data as CSV" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={handleDownloadCSV}>
                        <QueryStatsIcon sx={topBarIconSx.queryStats}></QueryStatsIcon>
                        <DownloadIcon sx={topBarIconSx.download}></DownloadIcon>
                      </Button>
                    </Tooltip>
                    <DialogSelect
                      buttonTitle="Time Range"
                      rangeObj={rangeObj}
                      dialogTitle="Choose Time Range"
                      color="white"
                      onSelectionChanged={changeTimeRange}
                      onSelectionOK={setSensorTimeRangeValue}
                      selectLabel="Time Range"
                      selectOptions={TimeRangeOptions}
                      maxDateRangeDays={31}
                      default={TimeRangeOptions[1]}
                      onClose={refreshContent}
                      toolTip="When choosing 14 days or longer, the measurements intervals will be 15 min instead of 5."></DialogSelect>
                    <IconButton sx={{ minWidth: '30px' }} color="light" onClick={() => refreshContent()}>
                      <Refresh sx={topBarIconSx.refresh} />
                    </IconButton>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack
                    direction="row"
                    marginLeft={2}
                    spacing={2}
                    alignItems="end"
                    className="top-bar"
                    sx={{ display: 'flex', alignItems: 'center' }}>
                    {renderSensorDisplayName()}
                    <Icon fontSize="large" style={{ color: 'white' }}>
                      {isInactive ? 'sensors_off' : 'sensors'}
                    </Icon>
                    {lastUpdate !== 0 && (
                      <MDTypography variant="subtitle" color="white">
                        Last Update: {getUpdateLastUpdateTime()}
                      </MDTypography>
                    )}
                    {loading && <CircularProgress style={{ color: 'white' }} />}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0}
                    sx={{
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'flex-end',
                    }}>
                    <Tooltip {...sensorTooltipProps} title="Edit sensor info" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={() => {
                          deviceType.startsWith('Controller')
                            ? navigate(`/manage-device/controller/${sensorId}`)
                            : navigate(`/manage-device/sensor/${sensorId}`);
                        }}>
                        <SettingsIcon sx={topBarIconSx.settings} />
                      </Button>
                    </Tooltip>
                    <Tooltip {...sensorTooltipProps} title="See charts" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={() => {
                          setMobileChartsView('filters');
                          setMobileChartsOpen(true);
                        }}>
                        <TimelineIcon sx={{ scale: '2.4' }} />
                        <OpenInNewIcon sx={{ scale: '1.4', ml: 0.2, mt: 1.5 }} />
                      </Button>
                    </Tooltip>
                    <Tooltip {...sensorTooltipProps} title="Download alerts to csv file" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={() => setTriggerDownloadAlertsCsv(prev => prev + 1)}>
                        <ErrorIcon sx={{ scale: '1.8' }}></ErrorIcon>
                        <DownloadIcon sx={topBarIconSx.download}></DownloadIcon>
                      </Button>
                    </Tooltip>
                    <Tooltip {...sensorTooltipProps} title="Download charts data as CSV" placement="top" arrow>
                      <Button
                        className="top-bar-btn"
                        sx={sensorStatusButtonSx}
                        color="success"
                        variant="contained"
                        onClick={handleDownloadCSV}>
                        <QueryStatsIcon sx={topBarIconSx.queryStats}></QueryStatsIcon>
                        <DownloadIcon sx={topBarIconSx.download}></DownloadIcon>
                      </Button>
                    </Tooltip>
                    <DialogSelect
                      buttonTitle="Time Range"
                      rangeObj={rangeObj}
                      dialogTitle="Choose Time Range"
                      color="white"
                      onSelectionChanged={changeTimeRange}
                      onSelectionOK={setSensorTimeRangeValue}
                      selectLabel="Time Range"
                      selectOptions={TimeRangeOptions}
                      maxDateRangeDays={31}
                      default={TimeRangeOptions[1]}
                      onClose={refreshContent}
                      toolTip="When choosing 14 days or longer, the measurements intervals will be 15 min instead of 5."></DialogSelect>
                    <IconButton
                      sx={{
                        minWidth: '30px',
                      }}
                      color="light"
                      onClick={() => refreshContent()}>
                      <Refresh sx={topBarIconSx.refresh} />
                    </IconButton>
                  </Stack>
                </Stack>
              )}
            </MDBox>
            <Box
              container
              sx={{
                overflowX: 'visible',
                overflowY: 'visible',
                display: 'flex',
                height: 'fit-content',
                flexDirection: {
                  xs: 'column',
                  lg: 'row',
                },
                justifyContent: 'space-between',
              }}
              className="bodycontainer">
              <Box className="curDataContainer" item xs={12} md={2} sx={{ height: 'fit-content' }}>
                {Object.keys(lastMeasurements).length > 0 && (
                  <CurrentData
                    cal={getLastValue(Measurement.CAL)}
                    temperature={getLastValue(Measurement.Temperature)}
                    rh={getLastValue(Measurement.RelativeHumidity)}
                    co2Ratio={getLastValue(Measurement.Co2Ratio)}
                    co2={getLastValue(Measurement.Co2)}
                    pm2={getLastValue(Measurement.PM2)}
                    pm10={getLastValue(Measurement.PM10)}
                    comfortLevel={getLastValue(Measurement.COMFORT_LEVEL)}
                    tvocMass={getLastValue(Measurement.TVOC)}
                    hideTvocMass={deviceType === 'Sensor-SPS30'}
                    occupancy={getLastValue(Measurement.occupancy)}
                    nox={getLastValue('nox')}
                    thermalJustified={getLastValue(Measurement.thermal_justified)}
                    thermalSaveable={getLastValue(Measurement.thermal_saveable)}
                    thermalRequested={getLastValue(Measurement.thermal_requested)}
                    airJustified={getLastValue(Measurement.air_justified)}
                    airSaveable={getLastValue(Measurement.air_saveable)}
                    airRequested={getLastValue(Measurement.air_requested)}
                    title="Real time data"
                  />
                )}
                {/* {Object.keys(minMaxRangeMeasurements).length !== 0 && <SensorHealth multiSensors={false} />} */}
              </Box>
              <Box
                item
                justifyContent="space-between"
                paddingTop={1}
                className="ChartsBox"
                xs={10}
                sx={{
                  padding: {
                    xs: '8px 0',
                    sm: '0px',
                  },
                  paddingLeft: {
                    xs: 0,
                    sm: '1rem',
                  },
                  marginTop: {
                    xs: '2rem',
                    lg: '0',
                  },
                  flexGrow: '1',
                  minWidth: '0',
                  height: 'fit-content',
                }}>
                {/* {!isSmallScreen && (
                  <GraphsFilterBar
                    hasNox={hasNoxData}
                    hasRadar={hasRadarData}
                    hasDpMean={hasDpMeanData}
                    hideTvoc={deviceType === 'Sensor-SPS30'}
                  />
                )} */}
                {hasFetchedRangeData ? (
                  !isSmallScreen && <Charts onChartRangeSelect={handleChartRangeSelect} chartRangeButtons={chartRangeButtons} />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}>
                    {hasFetchedRangeData === false && !loading && (
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          p: 2,
                          maxWidth: 400,
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'grey.50',
                        }}>
                        <QueryStatsIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <CardContent sx={{ p: 0 }}>
                          <Typography variant="subtitle1" color="text.primary" fontWeight="bold">
                            No Data Available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No data available for the selected sensor for the selected time range. Try changing the time range.
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                )}
                {<AlertSummary showSensorId={false} sensorId={sensorId} downloadCsv={triggerDownloadAlertsCsv} />}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default SensorPage;
